import * as React from 'react'
import styled from 'styled-components'
import { FleetNavigationLayout } from '../components/fleet-navigation-sidebar'

export type PipelineStep =
  | 'spawned'
  | 'analyze'
  | 'pipeline'
  | 'implement'
  | 'code-review'
  | 'create-pr'
  | 'comet-review'
  | 'done'
  | 'blocked'
  | 'unknown'

export type AgentState = 'ACTIVE' | 'WORKING' | 'IDLE' | 'STALE' | 'STUCK'
type InstructStatus = 'idle' | 'sending' | 'sent' | 'error'

export interface PipelineSession {
  session_id: string
  pid: number
  branch: string
  cwd: string
  step: PipelineStep
  step_index: number
  spec: string
  last_tool: string | null
  last_msg: string
  last_turn_at: string
  minutes_in_step: number
  is_stale: boolean
  is_stuck: boolean
  agent_state: AgentState
  tasks_done: number
  tasks_total: number
  pr_url?: string
  pr_number?: number
}

export interface PipelineData {
  sessions: PipelineSession[]
  by_step: Record<string, PipelineSession[]>
  summary: {
    total_live: number
    stuck: number
    stale: number
    at_pr_gate: number
    done: number
  }
}

interface StepDefinition {
  key: PipelineStep
  label: string
  color: string
  description: string
  gate?: boolean
}

interface ProjectProgress {
  name: string
  progress: number
  status: 'active' | 'paused' | 'completed' | 'blocked'
  description: string
  lastActivity: string
  color: string
}

const STATUS_ENDPOINT = '/api/fleet/pipeline-status'
const INSTRUCT_ENDPOINT = '/api/fleet/instruct'
const POLL_INTERVAL_MS = 10_000

const STEPS: readonly StepDefinition[] = [
  { key: 'spawned', label: 'Spawned', color: '#9CA6AD', description: 'Agent initialized' },
  { key: 'analyze', label: 'Analyze', color: '#3292C0', description: 'Understanding requirements' },
  { key: 'pipeline', label: 'Pipeline', color: '#6366f1', description: 'Setting up workflow' },
  { key: 'implement', label: 'Implement', color: '#33AA40', description: 'Building solution' },
  { key: 'code-review', label: 'Code Review', color: '#F5A623', description: 'Quality assurance' },
  { key: 'create-pr', label: 'Create PR', color: '#f59e0b', description: 'Submitting changes', gate: true },
  { key: 'comet-review', label: 'Comet Review', color: '#ec4899', description: 'Final validation', gate: true },
  { key: 'done', label: 'Done', color: '#33AA40', description: 'Completed successfully' },
  { key: 'blocked', label: 'Blocked', color: '#D0021B', description: 'Needs intervention' },
  { key: 'unknown', label: 'Unknown', color: '#9CA6AD', description: 'Unmapped or legacy state' },
]

const STEP_KEYS = new Set(STEPS.map((step) => step.key))

const ACTIVE_PROJECTS: readonly ProjectProgress[] = [
  {
    name: 'UI Integration',
    progress: 15,
    status: 'active',
    description: 'React component integration and styling updates',
    lastActivity: '2 mins ago',
    color: '#3292C0',
  },
  {
    name: 'Spequafy Pipeline',
    progress: 20,
    status: 'active',
    description: 'Automated specification processing pipeline',
    lastActivity: '5 mins ago',
    color: '#33AA40',
  },
  {
    name: 'Browser Agents',
    progress: 85,
    status: 'active',
    description: 'Automated browser control and testing agents',
    lastActivity: '1 min ago',
    color: '#F5A623',
  },
  {
    name: 'Fleet Orchestration',
    progress: 60,
    status: 'active',
    description: 'Multi-agent coordination and resource management',
    lastActivity: '3 mins ago',
    color: '#7D6CFF',
  },
]

const STATE_COLOR: Record<AgentState, string> = {
  ACTIVE: '#33AA40',
  WORKING: '#33AA40',
  IDLE: '#9CA6AD',
  STALE: '#F5A623',
  STUCK: '#D0021B',
}

const Page = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
`

const PageHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  flex-wrap: wrap;
  padding: 14px 16px;
  border: 1px solid rgba(156, 166, 173, 0.32);
  border-radius: ${(p) => p.theme.radius};
  background:
    linear-gradient(135deg, rgba(51, 170, 64, 0.11), rgba(50, 146, 192, 0.07)),
    #1f313a;
`

const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`

const Eyebrow = styled.span`
  color: ${(p) => p.theme.accent};
  font-size: 11px;
  font-family: 'NunitoSansSemiBold';
  line-height: 15px;
`

const Heading = styled.h3`
  margin: 0;
  font-size: 24px;
  line-height: 30px;
  font-family: 'NunitoSansSemiBold';
  color: ${(p) => p.theme.white};
`

const Subtitle = styled.span`
  font-size: 13px;
  color: ${(p) => p.theme.white};
  opacity: 0.68;
  line-height: 18px;
`

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`

const LastUpdated = styled.span`
  font-size: 11px;
  color: ${(p) => p.theme.white};
  opacity: 0.58;
`

const RefreshButton = styled.button`
  padding: 6px 12px;
  border-radius: ${(p) => p.theme.radius};
  font-size: 12px;
  font-family: 'NunitoSansSemiBold';
  cursor: pointer;
  min-height: 34px;
  border: 1px solid ${(p) => p.theme.input.default};
  background-color: transparent;
  color: ${(p) => p.theme.white};

  &:hover:not(:disabled) {
    background-color: ${(p) => p.theme.button.primary.default};
    border-color: ${(p) => p.theme.button.primary.default};
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  &:focus-visible {
    outline: none;
    box-shadow: 0 0 0 2px ${(p) => p.theme.white}, 0 0 0 4px ${(p) => p.theme.accent};
  }
`

const ToggleButton = styled(RefreshButton)<{ $active: boolean }>`
  border-color: ${(p) => (p.$active ? p.theme.accent : p.theme.input.default)};
  background-color: ${(p) => (p.$active ? 'rgba(51, 170, 64, 0.18)' : 'transparent')};
  color: ${(p) => (p.$active ? p.theme.accent : p.theme.white)};
`

const SummaryRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const Pill = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  min-height: 22px;
  padding: 2px 9px;
  border-radius: 999px;
  border: 1px solid ${(p) => p.$color};
  background-color: ${(p) => `${p.$color}22`};
  color: ${(p) => p.$color};
  font-size: 11px;
  font-family: 'NunitoSansSemiBold';
  white-space: nowrap;
`

const SectionHeader = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
`

const SectionTitle = styled.h4`
  margin: 0;
  color: ${(p) => p.theme.white};
  font-size: 14px;
  line-height: 20px;
  font-family: 'NunitoSansSemiBold';
`

const SectionMeta = styled.span`
  color: ${(p) => p.theme.white};
  opacity: 0.52;
  font-size: 11px;
`

const ProjectGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 10px;
`

const ProjectCard = styled.article`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  padding: 12px;
  border: 1px solid rgba(156, 166, 173, 0.28);
  border-radius: ${(p) => p.theme.radius};
  background-color: #1f313a;
  color: ${(p) => p.theme.white};
`

const ProjectTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`

const ProjectName = styled.strong`
  font-size: 13px;
  line-height: 18px;
  font-family: 'NunitoSansSemiBold';
`

const ProjectStatus = styled.span<{ $status: ProjectProgress['status'] }>`
  flex: 0 0 auto;
  border: 1px solid ${(p) => (p.$status === 'blocked' ? '#D0021B' : p.$status === 'paused' ? '#9CA6AD' : '#33AA40')};
  border-radius: 999px;
  padding: 1px 7px;
  color: ${(p) => (p.$status === 'blocked' ? '#FF5656' : p.$status === 'paused' ? '#9CA6AD' : '#33AA40')};
  background-color: ${(p) => (p.$status === 'blocked' ? 'rgba(208, 2, 27, 0.12)' : p.$status === 'paused' ? 'rgba(156, 166, 173, 0.12)' : 'rgba(51, 170, 64, 0.12)')};
  font-size: 10px;
  font-family: 'NunitoSansSemiBold';
  text-transform: uppercase;
`

const ProjectDescription = styled.p`
  margin: 0;
  min-height: 34px;
  color: ${(p) => p.theme.white};
  opacity: 0.62;
  font-size: 12px;
  line-height: 17px;
`

const ProjectProgressTrack = styled.div`
  height: 6px;
  border-radius: 999px;
  background-color: rgba(255, 255, 255, 0.12);
  overflow: hidden;
`

const ProjectProgressFill = styled.div<{ $pct: number; $color: string }>`
  width: ${(p) => `${p.$pct}%`};
  height: 100%;
  background-color: ${(p) => p.$color};
`

const ProjectFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: ${(p) => p.theme.white};
  opacity: 0.58;
  font-size: 11px;
`

const Notice = styled.div<{ $danger?: boolean }>`
  border: 1px solid ${(p) => (p.$danger ? '#D0021B' : p.theme.input.default)};
  background-color: ${(p) => p.theme.pageBack};
  color: ${(p) => (p.$danger ? '#FF5656' : p.theme.white)};
  border-radius: ${(p) => p.theme.radius};
  padding: 10px 12px;
  font-size: 12px;
`

const Board = styled.div`
  display: flex;
  min-height: calc(100vh - 420px);
  height: calc(100vh - 420px);
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  background-color: #142630;
  border: 1px solid ${(p) => p.theme.input.default};
  border-radius: ${(p) => p.theme.radius};

  @media (max-width: 768px) {
    height: auto;
    min-height: 520px;
    flex-direction: column;
  }
`

const Lane = styled.section`
  display: flex;
  flex: 0 0 226px;
  min-width: 210px;
  max-width: 250px;
  flex-direction: column;
  border-right: 1px solid rgba(255, 255, 255, 0.12);
  padding: 0 8px 8px;

  @media (max-width: 768px) {
    max-width: none;
    border-right: 0;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
  }
`

const LaneHeader = styled.header<{ $color: string }>`
  position: sticky;
  top: 0;
  z-index: 1;
  padding: 10px 0 9px;
  margin-bottom: 8px;
  background-color: #142630;
  border-bottom: 2px solid ${(p) => p.$color};
`

const LaneTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-bottom: 3px;
`

const LaneName = styled.span<{ $color: string }>`
  color: ${(p) => p.$color};
  font-size: 11px;
  font-family: 'NunitoSansSemiBold';
  letter-spacing: 0;
  text-transform: uppercase;
`

const LaneDescription = styled.div`
  min-height: 28px;
  color: ${(p) => p.theme.white};
  opacity: 0.48;
  font-size: 10px;
  line-height: 14px;
  margin-bottom: 7px;
`

const GateBadge = styled.span`
  border: 1px solid #ec4899;
  border-radius: 3px;
  padding: 0 4px;
  color: #ec4899;
  background-color: rgba(236, 72, 153, 0.12);
  font-size: 9px;
  font-family: 'NunitoSansSemiBold';
`

const LaneMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
`

const CountBadge = styled.span`
  min-width: 18px;
  padding: 1px 7px;
  border-radius: 999px;
  color: ${(p) => p.theme.white};
  background-color: rgba(255, 255, 255, 0.12);
  font-size: 11px;
  font-family: 'NunitoSansSemiBold';
  text-align: center;
`

const StuckText = styled.span`
  color: #FF5656;
  font-size: 10px;
`

const LaneBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-bottom: 8px;
`

const EmptyLane = styled.div`
  padding-top: 22px;
  color: ${(p) => p.theme.white};
  opacity: 0.35;
  font-size: 12px;
  text-align: center;
`

const SessionCardWrap = styled.article<{ $state: AgentState }>`
  border: 1px solid ${(p) => STATE_COLOR[p.$state]};
  background-color: rgba(255, 255, 255, 0.07);
  border-radius: ${(p) => p.theme.radius};
  padding: 8px 10px;
  margin-bottom: 7px;
  font-size: 11px;
  color: ${(p) => p.theme.white};
`

const SessionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  min-width: 0;
  margin-bottom: 6px;
`

const StatusDot = styled.span<{ $state: AgentState }>`
  width: 7px;
  height: 7px;
  margin-top: 3px;
  border-radius: 50%;
  background-color: ${(p) => STATE_COLOR[p.$state]};
  box-shadow: 0 0 5px ${(p) => STATE_COLOR[p.$state]};
  flex: 0 0 auto;
`

const SessionSpec = styled.strong`
  min-width: 0;
  flex: 1;
  color: ${(p) => p.theme.white};
  line-height: 1.28;
  word-break: break-word;
`

const StateBadge = styled.span<{ $state: AgentState }>`
  border: 1px solid ${(p) => STATE_COLOR[p.$state]};
  border-radius: 999px;
  padding: 1px 6px;
  color: ${(p) => STATE_COLOR[p.$state]};
  background-color: ${(p) => `${STATE_COLOR[p.$state]}22`};
  font-size: 9px;
  font-family: 'NunitoSansSemiBold';
  white-space: nowrap;
`

const ProgressTrack = styled.div`
  height: 4px;
  border-radius: 999px;
  background-color: rgba(255, 255, 255, 0.14);
  overflow: hidden;
  margin-bottom: 5px;
`

const ProgressFill = styled.div<{ $pct: number; $state: AgentState }>`
  width: ${(p) => `${p.$pct}%`};
  height: 100%;
  background-color: ${(p) => (p.$state === 'STUCK' ? '#D0021B' : STATE_COLOR[p.$state])};
`

const ProgressLabel = styled.div`
  color: ${(p) => p.theme.white};
  opacity: 0.5;
  font-size: 10px;
  text-align: right;
  margin-top: -2px;
  margin-bottom: 5px;
`

const SessionLine = styled.div<{ $alert?: boolean; $warn?: boolean }>`
  color: ${(p) => (p.$alert ? '#FF5656' : p.$warn ? '#F5A623' : p.theme.white)};
  opacity: ${(p) => (p.$alert || p.$warn ? 1 : 0.55)};
  font-size: 10px;
  line-height: 1.45;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
`

const FooterRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
`

const Mono = styled.span`
  min-width: 0;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(p) => p.theme.white};
  opacity: 0.45;
  font-family: monospace;
  font-size: 10px;
`

const SmallButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 3px;
  background-color: rgba(255, 255, 255, 0.07);
  color: ${(p) => p.theme.white};
  opacity: 0.72;
  cursor: pointer;
  padding: 3px 6px;
  font-size: 10px;

  &:hover:not(:disabled) {
    opacity: 1;
    border-color: ${(p) => p.theme.accent};
  }

  &:disabled {
    opacity: 0.35;
    cursor: not-allowed;
  }
`

const PrLink = styled.a`
  display: inline-flex;
  color: #ec4899;
  text-decoration: none;
  font-size: 10px;
  margin-top: 3px;

  &:hover {
    text-decoration: underline;
  }
`

const ModalScrim = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background-color: rgba(0, 0, 0, 0.68);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
`

const Modal = styled.div`
  width: min(440px, 100%);
  border: 1px solid ${(p) => p.theme.input.default};
  border-radius: ${(p) => p.theme.radius};
  background-color: ${(p) => p.theme.pageBack};
  padding: 18px;
  color: ${(p) => p.theme.white};
`

const ModalTitle = styled.h4`
  margin: 0 0 12px;
  font-size: 15px;
  font-family: 'NunitoSansSemiBold';
`

const ModalSid = styled.span`
  color: ${(p) => p.theme.accent};
  font-family: monospace;
`

const InstructionInput = styled.textarea`
  width: 100%;
  min-height: 104px;
  box-sizing: border-box;
  resize: vertical;
  border: 1px solid ${(p) => p.theme.input.default};
  border-radius: ${(p) => p.theme.radius};
  background-color: ${(p) => p.theme.input.default};
  color: ${(p) => p.theme.white};
  padding: 8px 10px;
  font-size: 13px;
  font-family: inherit;

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${(p) => p.theme.accent};
  }
`

const ModalActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 12px;
`

const ModalButton = styled.button<{ $primary?: boolean; $danger?: boolean }>`
  min-height: 34px;
  padding: 6px 14px;
  border: 1px solid ${(p) => (p.$danger ? '#D0021B' : p.$primary ? p.theme.button.primary.default : p.theme.input.default)};
  border-radius: ${(p) => p.theme.radius};
  background-color: ${(p) => (p.$danger ? '#D0021B' : p.$primary ? p.theme.button.primary.default : 'transparent')};
  color: ${(p) => p.theme.white};
  cursor: pointer;
  font-size: 13px;
  font-family: 'NunitoSansSemiBold';

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`

export function formatElapsed(minutes: number): string {
  if (!Number.isFinite(minutes) || minutes < 0) return '0m'
  const totalMinutes = Math.round(minutes)
  if (totalMinutes < 60) return `${totalMinutes}m`
  return `${Math.floor(totalMinutes / 60)}h${totalMinutes % 60}m`
}

export function truncate(value: string, length: number): string {
  if (value.length <= length) return value
  if (length <= 3) return '.'.repeat(Math.max(0, length))
  return `${value.slice(0, length - 3)}...`
}

export function normalizeData(data: PipelineData): PipelineData {
  const byStep: Record<string, PipelineSession[]> = {}
  STEPS.forEach((step) => {
    byStep[step.key] = []
  })

  Object.entries(data.by_step ?? {}).forEach(([key, value]) => {
    if (!Array.isArray(value)) return
    if (STEP_KEYS.has(key as PipelineStep)) {
      byStep[key] = value
      return
    }
    byStep.unknown = [
      ...byStep.unknown,
      ...value.map((session) => ({
        ...session,
        step: 'unknown' as const,
      })),
    ]
  })

  return {
    sessions: Array.isArray(data.sessions) ? data.sessions : [],
    by_step: byStep,
    summary: {
      total_live: Number(data.summary?.total_live ?? data.sessions?.length ?? 0),
      stuck: Number(data.summary?.stuck ?? 0),
      stale: Number(data.summary?.stale ?? 0),
      at_pr_gate: Number(data.summary?.at_pr_gate ?? 0),
      done: Number(data.summary?.done ?? 0),
    },
  }
}

async function fetchJson<T>(url: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers)
  if (!headers.has('Accept')) headers.set('Accept', 'application/json')
  const res = await fetch(url, {
    credentials: 'include',
    ...init,
    headers,
  })
  const contentType = res.headers.get('content-type') ?? ''
  if (!res.ok) throw new Error(`Fleet API request failed (${res.status})`)
  if (!contentType.includes('application/json')) {
    throw new Error('Fleet API returned non-JSON. Check the Equa fleet route.')
  }
  return res.json()
}

function getSessionState(session: PipelineSession): AgentState {
  if (session.agent_state) return session.agent_state
  if (session.is_stuck) return 'STUCK'
  if (session.is_stale) return 'STALE'
  return 'IDLE'
}

function ProjectCardView({ project }: { project: ProjectProgress }) {
  return (
    <ProjectCard>
      <ProjectTop>
        <ProjectName>{project.name}</ProjectName>
        <ProjectStatus $status={project.status}>{project.status}</ProjectStatus>
      </ProjectTop>
      <ProjectDescription>{project.description}</ProjectDescription>
      <ProjectProgressTrack aria-label={`${project.name} ${project.progress}% complete`}>
        <ProjectProgressFill $pct={project.progress} $color={project.color} />
      </ProjectProgressTrack>
      <ProjectFooter>
        <span>{project.progress}% complete</span>
        <span>{project.lastActivity}</span>
      </ProjectFooter>
    </ProjectCard>
  )
}

function SessionCard({ session, onInstruct }: { session: PipelineSession; onInstruct: (sessionId: string) => void }) {
  const state = getSessionState(session)
  const hasProgress = session.tasks_total > 0
  const pct = hasProgress ? Math.min(100, Math.round((session.tasks_done / session.tasks_total) * 100)) : 0
  const isWarn = session.is_stale && !session.is_stuck

  return (
    <SessionCardWrap $state={state}>
      <SessionHeader>
        <StatusDot $state={state} />
        <SessionSpec>{session.spec || 'unknown'}</SessionSpec>
        <StateBadge $state={state}>{state}</StateBadge>
      </SessionHeader>

      {hasProgress ? (
        <>
          <ProgressTrack aria-label={`${session.tasks_done} of ${session.tasks_total} tasks complete`}>
            <ProgressFill $pct={pct} $state={state} />
          </ProgressTrack>
          <ProgressLabel>{session.tasks_done}/{session.tasks_total}</ProgressLabel>
        </>
      ) : null}

      {session.branch ? <SessionLine title={session.branch}>{truncate(session.branch, 34)}</SessionLine> : null}
      <SessionLine $alert={session.is_stuck} $warn={isWarn}>
        {formatElapsed(session.minutes_in_step)} in step
        {session.last_tool ? ` · ${session.last_tool}` : ''}
      </SessionLine>

      {session.pr_url ? (
        <PrLink href={session.pr_url} target="_blank" rel="noopener noreferrer">
          PR #{session.pr_number ?? 'open'}
        </PrLink>
      ) : null}

      <FooterRow>
        <Mono title={session.session_id}>{session.session_id.slice(0, 8)}</Mono>
        <SmallButton type="button" onClick={() => onInstruct(session.session_id)}>
          Instruct
        </SmallButton>
      </FooterRow>
    </SessionCardWrap>
  )
}

function LaneColumn({
  step,
  sessions,
  onInstruct,
}: {
  step: StepDefinition
  sessions: PipelineSession[]
  onInstruct: (sessionId: string) => void
}) {
  const stuckCount = sessions.filter((session) => session.is_stuck).length

  return (
    <Lane aria-label={`${step.label} pipeline lane`}>
      <LaneHeader $color={step.color}>
        <LaneTitle>
          <LaneName $color={step.color}>{step.label}</LaneName>
          {step.gate ? <GateBadge>GATE</GateBadge> : null}
        </LaneTitle>
        <LaneDescription>{step.description}</LaneDescription>
        <LaneMeta>
          <CountBadge>{sessions.length}</CountBadge>
          {stuckCount > 0 ? <StuckText>{stuckCount} stuck</StuckText> : null}
        </LaneMeta>
      </LaneHeader>
      <LaneBody>
        {sessions.length === 0 ? (
          <EmptyLane>No active sessions</EmptyLane>
        ) : (
          sessions.map((session) => (
            <SessionCard key={session.session_id} session={session} onInstruct={onInstruct} />
          ))
        )}
      </LaneBody>
    </Lane>
  )
}

export const FleetPipelinePage: React.FC = () => {
  const [data, setData] = React.useState<PipelineData | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [refreshing, setRefreshing] = React.useState(false)
  const [realTimeEnabled, setRealTimeEnabled] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)
  const [lastUpdate, setLastUpdate] = React.useState<Date | null>(null)
  const [instructSid, setInstructSid] = React.useState<string | null>(null)
  const [instructText, setInstructText] = React.useState('')
  const [instructStatus, setInstructStatus] = React.useState<InstructStatus>('idle')

  const loadData = React.useCallback(async () => {
    setRefreshing(true)
    try {
      const next = await fetchJson<PipelineData>(STATUS_ENDPOINT)
      setData(normalizeData(next))
      setLastUpdate(new Date())
      setError(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load fleet pipeline')
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  React.useEffect(() => {
    void loadData()
  }, [loadData])

  React.useEffect(() => {
    if (!realTimeEnabled) return undefined
    const timer = window.setInterval(() => void loadData(), POLL_INTERVAL_MS)
    return () => window.clearInterval(timer)
  }, [loadData, realTimeEnabled])

  const handleInstruct = React.useCallback(async () => {
    if (!instructSid || !instructText.trim()) return
    setInstructStatus('sending')
    try {
      await fetchJson(INSTRUCT_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: instructSid, instruction: instructText.trim() }),
      })
      setInstructStatus('sent')
      window.setTimeout(() => {
        setInstructSid(null)
        setInstructText('')
        setInstructStatus('idle')
      }, 1200)
    } catch {
      setInstructStatus('error')
    }
  }, [instructSid, instructText])

  const summary = data?.summary

  return (
    <FleetNavigationLayout>
      <Page>
        <PageHeader>
          <TitleGroup>
            <Eyebrow>Command Center</Eyebrow>
            <Heading>Fleet Dashboard</Heading>
            <Subtitle>Real-time agent coordination, Spequa gates, browser review, and PR handoff status.</Subtitle>
          </TitleGroup>
          <HeaderActions>
            <ToggleButton
              type="button"
              $active={realTimeEnabled}
              onClick={() => setRealTimeEnabled((enabled) => !enabled)}
            >
              Real-time {realTimeEnabled ? 'ON' : 'OFF'}
            </ToggleButton>
            {lastUpdate ? <LastUpdated>{lastUpdate.toLocaleTimeString()}</LastUpdated> : null}
            <RefreshButton type="button" onClick={loadData} disabled={refreshing}>
              {refreshing ? 'Refreshing' : 'Refresh'}
            </RefreshButton>
          </HeaderActions>
        </PageHeader>

        {summary ? (
          <SummaryRow aria-label="Fleet pipeline summary">
            <Pill $color="#33AA40">{summary.total_live} live</Pill>
            <Pill $color="#D0021B">{summary.stuck} stuck</Pill>
            <Pill $color="#F5A623">{summary.stale} stale</Pill>
            {summary.at_pr_gate > 0 ? <Pill $color="#ec4899">{summary.at_pr_gate} at PR gate</Pill> : null}
          </SummaryRow>
        ) : null}

        {error ? <Notice $danger>{error}</Notice> : null}
        {loading ? <Notice>Loading fleet pipeline...</Notice> : null}

        <SectionHeader id="active-projects">
          <SectionTitle>Active Projects</SectionTitle>
          <SectionMeta>Native Equa command-center surface</SectionMeta>
        </SectionHeader>
        <ProjectGrid>
          {ACTIVE_PROJECTS.map((project) => (
            <ProjectCardView key={project.name} project={project} />
          ))}
        </ProjectGrid>

        <SectionHeader id="spequafy-pipeline">
          <SectionTitle>Spequafy Pipeline</SectionTitle>
          <SectionMeta>{data?.sessions.length ?? 0} live sessions</SectionMeta>
        </SectionHeader>
        <Board>
          {STEPS.map((step) => (
            <LaneColumn
              key={step.key}
              step={step}
              sessions={data?.by_step[step.key] ?? []}
              onInstruct={(sessionId) => {
                setInstructSid(sessionId)
                setInstructText('')
                setInstructStatus('idle')
              }}
            />
          ))}
        </Board>

        {instructSid ? (
          <ModalScrim
            onClick={(event) => {
              if (event.target === event.currentTarget) setInstructSid(null)
            }}
          >
            <Modal role="dialog" aria-modal="true" aria-labelledby="fleet-instruct-title">
              <ModalTitle id="fleet-instruct-title">
                Instruct <ModalSid>{instructSid.slice(0, 8)}</ModalSid>
              </ModalTitle>
              <InstructionInput
                autoFocus
                value={instructText}
                onChange={(event) => setInstructText(event.target.value)}
                placeholder="What should this agent do next?"
                onKeyDown={(event) => {
                  if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) void handleInstruct()
                  if (event.key === 'Escape') setInstructSid(null)
                }}
              />
              <ModalActions>
                <ModalButton type="button" onClick={() => setInstructSid(null)}>
                  Cancel
                </ModalButton>
                <ModalButton
                  type="button"
                  $primary={instructStatus !== 'error'}
                  $danger={instructStatus === 'error'}
                  disabled={instructStatus === 'sending' || !instructText.trim()}
                  onClick={() => void handleInstruct()}
                >
                  {instructStatus === 'sending'
                    ? 'Sending'
                    : instructStatus === 'sent'
                      ? 'Sent'
                      : instructStatus === 'error'
                        ? 'Failed'
                        : 'Send'}
                </ModalButton>
              </ModalActions>
            </Modal>
          </ModalScrim>
        ) : null}
      </Page>
    </FleetNavigationLayout>
  )
}

export default FleetPipelinePage
